/**
 * Accessibility utilities for improving keyboard navigation, screen reader support,
 * and focus management throughout the application.
 */

import { useEffect, useRef, useState, KeyboardEvent, RefObject } from 'react';

/**
 * Hook to trap focus within a container for modals, dialogs, etc.
 * @param isActive Whether the focus trap is active
 * @param onEscape Callback when Escape key is pressed
 * @param initialFocusRef Optional ref to element that should receive initial focus
 * @returns Ref to attach to the container element
 */
export const useFocusTrap = (
  isActive: boolean, 
  onEscape?: () => void,
  initialFocusRef?: RefObject<HTMLElement>
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!isActive || !containerRef.current) return;
    
    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"], [role="checkbox"], [role="radio"], [role="switch"], [role="menuitem"], [role="option"]'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    // Save the previously focused element to restore later
    const previouslyFocused = document.activeElement as HTMLElement;
    
    // Focus the initial element or first focusable element when the trap becomes active
    if (initialFocusRef && initialFocusRef.current) {
      initialFocusRef.current.focus();
    } else {
      firstElement.focus();
    }
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        e.preventDefault();
        onEscape();
        return;
      }
      
      if (e.key !== 'Tab') return;
      
      // Handle tab navigation to keep focus inside the container
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };
    
    container.addEventListener('keydown', handleKeyDown as any);
    
    // Add aria attributes for screen readers
    const originalRole = container.getAttribute('role');
    const originalAriaModal = container.getAttribute('aria-modal');
    
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-modal', 'true');
    
    return () => {
      container.removeEventListener('keydown', handleKeyDown as any);
      
      // Restore original attributes
      if (originalRole) {
        container.setAttribute('role', originalRole);
      } else {
        container.removeAttribute('role');
      }
      
      if (originalAriaModal) {
        container.setAttribute('aria-modal', originalAriaModal);
      } else {
        container.removeAttribute('aria-modal');
      }
      
      // Restore focus when the trap is deactivated
      if (previouslyFocused && previouslyFocused.focus) {
        // Use a small timeout to ensure focus happens after state updates
        setTimeout(() => {
          previouslyFocused.focus();
        }, 0);
      }
    };
  }, [isActive, onEscape, initialFocusRef]);
  
  return containerRef;
};

/**
 * Hook to manage keyboard navigation for a list of items
 * @param itemCount Number of items in the list
 * @param onSelect Callback when an item is selected
 * @param orientation Direction of navigation ('vertical' or 'horizontal')
 * @param loop Whether to loop from last to first item and vice versa
 * @param initialIndex Starting index for focus
 * @returns Object with current index and key handlers
 */
export const useKeyboardNavigation = (
  itemCount: number,
  onSelect: (index: number) => void,
  orientation: 'vertical' | 'horizontal' | 'both' = 'vertical',
  loop: boolean = true,
  initialIndex: number = 0
) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  
  const handleKeyDown = (e: KeyboardEvent) => {
    let newIndex = activeIndex;
    let handled = false;
    
    // Handle vertical navigation
    if (orientation === 'vertical' || orientation === 'both') {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          handled = true;
          if (activeIndex < itemCount - 1) {
            newIndex = activeIndex + 1;
          } else if (loop) {
            newIndex = 0;
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          handled = true;
          if (activeIndex > 0) {
            newIndex = activeIndex - 1;
          } else if (loop) {
            newIndex = itemCount - 1;
          }
          break;
      }
    }
    
    // Handle horizontal navigation
    if (orientation === 'horizontal' || orientation === 'both') {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          handled = true;
          if (activeIndex < itemCount - 1) {
            newIndex = activeIndex + 1;
          } else if (loop) {
            newIndex = 0;
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handled = true;
          if (activeIndex > 0) {
            newIndex = activeIndex - 1;
          } else if (loop) {
            newIndex = itemCount - 1;
          }
          break;
      }
    }
    
    // Handle direct navigation
    switch (e.key) {
      case 'Home':
        e.preventDefault();
        handled = true;
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        handled = true;
        newIndex = itemCount - 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handled = true;
        onSelect(activeIndex);
        break;
    }
    
    // Only update if we actually changed the index
    if (handled && newIndex !== activeIndex) {
      setActiveIndex(newIndex);
      return true;
    }
    
    return handled;
  };
  
  return { 
    activeIndex, 
    handleKeyDown, 
    setActiveIndex,
    // Helper function to get props for the active item
    getItemProps: (index: number) => ({
      tabIndex: index === activeIndex ? 0 : -1,
      'aria-selected': index === activeIndex,
      'data-active': index === activeIndex ? 'true' : undefined
    })
  };
};

/**
 * Hook to manage focus within a component when it mounts/unmounts
 * @param shouldFocus Whether focus should be managed
 * @param focusElementId ID of the element to focus (optional)
 * @param focusElementSelector CSS selector for the element to focus (optional)
 * @returns Ref to attach to the container element
 */
export const useFocusManagement = (
  shouldFocus: boolean = true,
  focusElementId?: string,
  focusElementSelector?: string
) => {
  const containerRef = useRef<HTMLElement>(null);
  
  useEffect(() => {
    if (!shouldFocus || !containerRef.current) return;
    
    let elementToFocus: HTMLElement | null = null;
    
    // Try to find the element by ID first
    if (focusElementId) {
      elementToFocus = document.getElementById(focusElementId);
    }
    
    // If not found by ID, try selector
    if (!elementToFocus && focusElementSelector && containerRef.current) {
      elementToFocus = containerRef.current.querySelector(focusElementSelector);
    }
    
    // If still not found, focus the first focusable element in the container
    if (!elementToFocus && containerRef.current) {
      const focusableElements = containerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"]'
      );
      
      if (focusableElements.length > 0) {
        elementToFocus = focusableElements[0];
      }
    }
    
    // Focus the element if found
    if (elementToFocus) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        elementToFocus?.focus();
      }, 100);
    }
  }, [shouldFocus, focusElementId, focusElementSelector]);
  
  return containerRef;
};

/**
 * Announce messages to screen readers using ARIA live regions
 */
export const useAnnounce = () => {
  const [message, setMessage] = useState('');
  
  const announce = (text: string, priority: 'polite' | 'assertive' = 'polite') => {
    // Update the message to trigger the screen reader announcement
    setMessage(`${text} ${Date.now()}`); // Adding timestamp ensures announcement even if text is the same
    // Note: priority parameter is available for future use with different live regions
  };
  
  return { announce, message };
};

/**
 * Skip to content link handler
 * @param contentId ID of the main content element
 */
export const useSkipToContent = (contentId: string) => {
  const handleSkip = () => {
    const contentElement = document.getElementById(contentId);
    if (contentElement) {
      contentElement.tabIndex = -1;
      contentElement.focus();
      // Reset tabIndex after focus to avoid persistent focus ring
      setTimeout(() => {
        contentElement.removeAttribute('tabIndex');
      }, 100);
    }
  };
  
  return { handleSkip };
};

/**
 * Format a number as currency for screen readers
 * @param amount The amount to format
 * @param currency The currency code (default: USD)
 * @returns Formatted string for screen readers
 */
export const formatCurrencyForScreenReader = (amount: number, currency = 'USD') => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  
  const formatted = formatter.format(Math.abs(amount));
  const prefix = amount >= 0 ? 'Profit of ' : 'Loss of ';
  
  return `${prefix}${formatted}`;
};

/**
 * Create an ID that's unique within the current document
 * @param prefix Prefix for the ID
 * @returns A unique ID string
 */
export const useUniqueId = (prefix: string) => {
  const [id] = useState(() => `${prefix}-${Math.random().toString(36).substr(2, 9)}`);
  return id;
};

/**
 * Check if high contrast mode is enabled
 * @returns Boolean indicating if high contrast mode is active and a function to toggle it manually
 */
export const useHighContrastMode = () => {
  const [isHighContrast, setIsHighContrast] = useState(() => {
    // Check for stored preference first
    const storedPreference = localStorage.getItem('high-contrast-mode');
    if (storedPreference !== null) {
      return storedPreference === 'true';
    }
    
    // Check for Windows high contrast mode or forced-colors
    return (
      window.matchMedia('(-ms-high-contrast: active)').matches ||
      window.matchMedia('(forced-colors: active)').matches
    );
  });
  
  useEffect(() => {
    // Create media query lists to watch for changes
    const msMediaQuery = window.matchMedia('(-ms-high-contrast: active)');
    const forcedColorsMediaQuery = window.matchMedia('(forced-colors: active)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsHighContrast(e.matches);
      localStorage.setItem('high-contrast-mode', String(e.matches));
    };
    
    // Add listeners for changes
    if (msMediaQuery.addEventListener) {
      msMediaQuery.addEventListener('change', handleChange);
      forcedColorsMediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      msMediaQuery.addListener(handleChange);
      forcedColorsMediaQuery.addListener(handleChange);
    }
    
    // Apply high contrast mode to document
    if (isHighContrast) {
      document.documentElement.classList.add('high-contrast-mode');
    } else {
      document.documentElement.classList.remove('high-contrast-mode');
    }
    
    return () => {
      if (msMediaQuery.removeEventListener) {
        msMediaQuery.removeEventListener('change', handleChange);
        forcedColorsMediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        msMediaQuery.removeListener(handleChange);
        forcedColorsMediaQuery.removeListener(handleChange);
      }
    };
  }, [isHighContrast]);
  
  // Function to manually toggle high contrast mode
  const toggleHighContrast = () => {
    const newValue = !isHighContrast;
    setIsHighContrast(newValue);
    localStorage.setItem('high-contrast-mode', String(newValue));
  };
  
  return { isHighContrast, toggleHighContrast };
};

/**
 * Hook to manage reduced motion preference
 * @returns Boolean indicating if reduced motion is preferred and a function to toggle it manually
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    // Check for stored preference first
    const storedPreference = localStorage.getItem('reduced-motion');
    if (storedPreference !== null) {
      return storedPreference === 'true';
    }
    
    // Check for system preference
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
      localStorage.setItem('reduced-motion', String(e.matches));
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    
    // Apply reduced motion class to document
    if (prefersReducedMotion) {
      document.documentElement.classList.add('reduced-motion');
    } else {
      document.documentElement.classList.remove('reduced-motion');
    }
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [prefersReducedMotion]);
  
  // Function to manually toggle reduced motion
  const toggleReducedMotion = () => {
    const newValue = !prefersReducedMotion;
    setPrefersReducedMotion(newValue);
    localStorage.setItem('reduced-motion', String(newValue));
  };
  
  return { prefersReducedMotion, toggleReducedMotion };
};

/**
 * Generate ARIA attributes for various UI patterns
 */
export const ariaAttributes = {
  /**
   * Generate ARIA attributes for a button that controls a dropdown
   */
  dropdown: (isOpen: boolean, controlsId: string) => ({
    'aria-expanded': isOpen,
    'aria-controls': controlsId,
    'aria-haspopup': true as const,
  }),
  
  /**
   * Generate ARIA attributes for a tab
   */
  tab: (isSelected: boolean, controlsId: string) => ({
    role: 'tab',
    'aria-selected': isSelected,
    'aria-controls': controlsId,
    tabIndex: isSelected ? 0 : -1,
  }),
  
  /**
   * Generate ARIA attributes for a tab panel
   */
  tabPanel: (labelledBy: string) => ({
    role: 'tabpanel',
    'aria-labelledby': labelledBy,
    tabIndex: 0,
  }),
  
  /**
   * Generate ARIA attributes for a modal dialog
   */
  dialog: (labelledBy: string, describedBy?: string) => ({
    role: 'dialog',
    'aria-modal': true,
    'aria-labelledby': labelledBy,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
  }),
  
  /**
   * Generate ARIA attributes for a tooltip
   */
  tooltip: (id: string) => ({
    role: 'tooltip',
    id,
  }),
  
  /**
   * Generate ARIA attributes for an element that has a tooltip
   */
  hasTooltip: (tooltipId: string) => ({
    'aria-describedby': tooltipId,
  }),
  
  /**
   * Generate ARIA attributes for a menu button
   */
  menuButton: (isOpen: boolean, controlsId: string) => ({
    'aria-expanded': isOpen,
    'aria-controls': controlsId,
    'aria-haspopup': 'menu' as const,
  }),
  
  /**
   * Generate ARIA attributes for a menu
   */
  menu: (labelledBy: string) => ({
    role: 'menu',
    'aria-labelledby': labelledBy,
  }),
  
  /**
   * Generate ARIA attributes for a menu item
   */
  menuItem: (isDisabled: boolean = false) => ({
    role: 'menuitem',
    'aria-disabled': isDisabled,
    tabIndex: isDisabled ? -1 : 0,
  }),
};

/**
 * Hook to detect and respond to color scheme preferences
 * @returns Object with color scheme preference and toggle function
 */
export const useColorScheme = () => {
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>(() => {
    // Check for stored preference first
    const storedPreference = localStorage.getItem('color-scheme');
    if (storedPreference === 'light' || storedPreference === 'dark') {
      return storedPreference;
    }
    
    // Check for system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't explicitly set a preference
      if (!localStorage.getItem('color-scheme')) {
        setColorScheme(e.matches ? 'dark' : 'light');
      }
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    
    // Apply color scheme class to document
    document.documentElement.classList.remove('light-mode', 'dark-mode');
    document.documentElement.classList.add(`${colorScheme}-mode`);
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [colorScheme]);
  
  // Function to toggle color scheme
  const toggleColorScheme = () => {
    const newScheme = colorScheme === 'light' ? 'dark' : 'light';
    setColorScheme(newScheme);
    localStorage.setItem('color-scheme', newScheme);
  };
  
  // Function to set a specific color scheme
  const setSpecificColorScheme = (scheme: 'light' | 'dark' | 'system') => {
    if (scheme === 'system') {
      localStorage.removeItem('color-scheme');
      const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setColorScheme(systemPreference);
    } else {
      setColorScheme(scheme);
      localStorage.setItem('color-scheme', scheme);
    }
  };
  
  return { colorScheme, toggleColorScheme, setColorScheme: setSpecificColorScheme };
};